import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Bernoulli's trials can tell you how many job applications to send",
  "description": "",
  "published": "2017-06-23T00:00:00.000Z",
  "redirect_from": ["/blog/bernoullis-trials-can-tell-many-job-applications-send/swizec/7677"],
  "categories": "Learning, Opinions, Personal",
  "hero": "./img/1lvVtEh.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A reader wrote to me bemoaning the difficulty of getting a job these days. She had read `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/the-most-pleasant-job-interview-i-have-ever-had/swizec/6121"
      }}>{`my old article about interviewing at Shopify`}</a>{` and thought something like `}<em parentName="p">{`"Damn that sounds so easy! Why is it so hard now?"`}</em></p>
    <blockquote>
      <p parentName="blockquote">{`I miss the days when getting a job didn’t mean jumping through rings of fire, not that I actually remember them, but I’ve heard stories! ~ Miss Anastacia`}</p>
    </blockquote>
    <p>{`I don't think it's gotten any harder or easier than it used to be. Think of it as a game of probabilities.`}</p>
    <p>{`Let's assume that a successful job application is a conversion funnel. Just like a funnel in sales, or marketing, or product signups. They're all similar in that you have a series of steps that produce one of two outcomes: Yes or No.`}</p>
    <p>{`In job applications you get an offer, or you don't. In sales, you make the sale, or not. In marketing, someone tries your app, or they don't. In product signups, they become a user, or not. When applying for conference talks, you get in, or you don't.`}</p>
    <p>{`This observation of a Yes or No result is important. It means we can model the probability of success after repeat attempts using `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Bernoulli_trial"
      }}>{`Bernoulli's trials`}</a>{`.`}</p>
    <blockquote>
      <p parentName="blockquote">{`In the theory of probability and statistics, a Bernoulli trial (or binomial trial) is a random experiment with exactly two possible outcomes, "success" and "failure", in which the probability of success is the same every time the experiment is conducted. It is named after Jacob Bernoulli, a 17th century Swiss mathematician.`}</p>
    </blockquote>
    <p>{`Those assumptions are not strictly true for us. You don't have the same probability of landing every job you apply for, some people are easier to sell to, some marketing campaigns are just better, and not all conferences have the same rejection rates.`}</p>
    <p>{`All of that is true. Life is fuzzy.`}</p>
    <p>{`We can rely on the law of `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Regression_toward_the_mean"
      }}>{`regression toward the mean`}</a>{` to gloss over that imperfection. After enough attempts we should have a clear conversion rate.`}</p>
    <p>{`Many people famous for speaking at tech conferences, for example, say that about 1 in 3 applications lands. That gives them a conversion rate of 30%.`}</p>
    <p>{`A job applicant with a smashing CV and amazing talent for applying only to jobs they're a great match for might have a conversion rate of, say, 50%.`}</p>
    <p>{`A fresh graduate using a spray and pray strategy might be closer to a 5% overall conversion rate. First contact to job offer.`}</p>
    <p>{`Job applications are often a long funnel and each step on the way has a Yes or No component. Each can be modeled as its own Bernoulli trial.`}</p>
    <ol>
      <li parentName="ol">{`First contact. You ping them or their recruiter pings you`}</li>
      <li parentName="ol">{`Initial phone call with recruiter person`}</li>
      <li parentName="ol">{`First real phone interview`}</li>
      <li parentName="ol">{`On-site interview lasting a few hours`}</li>
      <li parentName="ol">{`Chatting with offer giver so they can feel you out`}</li>
      <li parentName="ol">{`Job offer`}</li>
    </ol>
    <p>{`Many people don't realize this, but step 5 is where they decide what the offer is going to be. The best negotiator in the company feels you out and decides what to offer so it's juuuuust enough for you to say yes, but no more. That would be expensive.`}</p>
    <p>{`You often already have a job at this point. A red flag can still mess it up.`}</p>
    <p>{`Now let's say the probabilities of a Yes in each of those steps go something like this:`}</p>
    <ol>
      <li parentName="ol">{`Initial contact: 10%`}</li>
      <li parentName="ol">{`Soft phone interview: 80% (recruiters are optimistic, that's what pays them)`}</li>
      <li parentName="ol">{`Phone interview: 50% (you're a good engineer, but companies like to think they're tough)`}</li>
      <li parentName="ol">{`On-site interviews: 60% (you're already here, that's good)`}</li>
      <li parentName="ol">{`Chat with offer giver: 80% (only red flags will mess it up)`}</li>
      <li parentName="ol">{`Job offer`}</li>
    </ol>
    <p>{`That gives you an overall conversion rate of `}<inlineCode parentName="p">{`0.1*0.8*0.5*0.6*0.8 = 0.02 == 2%`}</inlineCode>{` which sounds really tough. I see now what Anastacia meant.`}</p>
    <p>{`Now if we plug that into the formula for `}<a parentName="p" {...{
        "href": "https://www.wolframalpha.com/input/?i=30+trials+p%3D0.02"
      }}>{`Bernoulli trials on WolframAlpha`}</a>{` we see that if you apply for 30 jobs, that gives you a 45% chance of landing at least one job. 100 applications increases that chance to 86%.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/aedb4ef8e7ff88998aceef9ddc3e90c9/21b4d/1lvVtEh.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "126.45739910313902%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'505\\'%20viewBox=\\'0%200%20400%20505\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M41%20123v31h99V92H41v31m2-29l-1%2030v29h4l4-1%201-2%201%202h86l1-30-1-28-47-1-48%201m117%20192l1%203c3%201%202%202%200%201l-2%201c-1%201%200%201%202%201l4-1c0-2%203-1%203%200l1%201h6c5-1%207-3%203-4v-1c4%200%202-2-2-2l-5%201h-1c-2-1-2-1-3%201l-3%201-1-1%202-1h-5m2%2041c-2%202-2%203-1%203l2%201c0%202%206%201%207%200%200-2%203-1%203%200s1%202%203%201c4-1%205-4%202-4v-1c3%200%202-2%200-2l-2%201h-1c-2-1-2-1-3%201l-3%201-1-1c3%200%202-2%200-2l-2%201h-1c-2-1-2-1-3%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/aedb4ef8e7ff88998aceef9ddc3e90c9/ca0a1/1lvVtEh.webp 223w", "/static/aedb4ef8e7ff88998aceef9ddc3e90c9/75680/1lvVtEh.webp 445w", "/static/aedb4ef8e7ff88998aceef9ddc3e90c9/8d1ba/1lvVtEh.webp 890w", "/static/aedb4ef8e7ff88998aceef9ddc3e90c9/af3f0/1lvVtEh.webp 1280w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/aedb4ef8e7ff88998aceef9ddc3e90c9/e92b6/1lvVtEh.png 223w", "/static/aedb4ef8e7ff88998aceef9ddc3e90c9/e66bf/1lvVtEh.png 445w", "/static/aedb4ef8e7ff88998aceef9ddc3e90c9/4ef49/1lvVtEh.png 890w", "/static/aedb4ef8e7ff88998aceef9ddc3e90c9/21b4d/1lvVtEh.png 1280w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/aedb4ef8e7ff88998aceef9ddc3e90c9/4ef49/1lvVtEh.png",
              "alt": "1lvVtEh",
              "title": "1lvVtEh",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`That sounds discouraging I know, but there are things you can do to improve your chances. You can improve your targeting to increase that first 10% chance. You can blog and go to meetups and build your network and skip that first step.`}</p>
    <p>{`When recruiters make the first contant, you have a higher chance. I promise. Conversion rate goes from the above, to something closer to `}<inlineCode parentName="p">{`0.8*0.5*0.6*0.8 = 0.19 == 19%`}</inlineCode>{`.`}</p>
    <p>{`That means you have an 87% chance of landing a job after just `}<a parentName="p" {...{
        "href": "https://www.wolframalpha.com/input/?i=10+trials+p%3D0.19"
      }}>{`10 Bernoulli trials`}</a>{`. Pretty good, no?`}</p>
    <p>{`Now of course all these probabilities are made up. They're different for every individual. They depend on experience, the companies you apply for, how much money you want.`}</p>
    <p>{`I promise that if you're a decent engineer and you're willing to work for $50k in Silicon Valley, you will have to fight off startup founders with a stick.`}</p>
    <p>{`Oh and what about our `}<em parentName="p">{`"Famous tech conference speaker with 30% conversion rate"`}</em>{` example? `}<a parentName="p" {...{
        "href": "https://www.wolframalpha.com/input/?i=7+trials+p%3D0.3"
      }}>{`7 CFP applications gives them a 91%`}</a>{` chance of giving a talk.`}</p>
    <p>{`And really that just means I'm a huge baby for having a single CFP rejected and thinking to myself screw conferences this is stupid I'm never CFPing ever again.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      